import { MenuItem, Select } from '@mui/material';
import { useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import ArrowDown from '@images/svg-icons/arrowDown.svg';

interface EyeProps {
    enabled: boolean;
    name: string;
    defaultValue?: string;
    values: string[] | number[];
}

interface DropdownProps {
    attribute: string;
    label: string;
    left?: EyeProps;
    right?: EyeProps;
}

const visuallyHiddenStyles = {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: 0,
    position: 'absolute' as const,
    width: '1px',
};

const sanitizeIdPart = value => `${value || ''}`.replace(/[^a-zA-Z0-9_-]/g, '-');

const menuOptions = {
    PaperProps: {
        style: {
            maxHeight: 220, // set a maximum height for the menu
        },
    },
};

const FormColumn = ({ attribute, label, left, right }: DropdownProps) => {
    const { register, control } = useFormContext();
    const idBaseRef = useRef(`presc-${Math.random().toString(36).slice(2, 10)}`);
    const idBase = idBaseRef.current;
    const fieldLabelId = `${idBase}-${sanitizeIdPart(attribute)}-field-label`;
    const rightEyeLabelId = `${idBase}-${sanitizeIdPart(
        right?.name || `${attribute}-right`
    )}-eye-label`;
    const leftEyeLabelId = `${idBase}-${sanitizeIdPart(
        left?.name || `${attribute}-left`
    )}-eye-label`;

    return (
        <>
            {(right?.values?.length || left?.values?.length) && (
                <div
                    className={`${attribute}-column param-column ${
                        right?.values.length === 1 && left?.values.length === 1
                            ? 'single-value'
                            : ''
                    }`}
                >
                    <div className={`param-label ${attribute}-label`}>
                        <label id={fieldLabelId}>{label}</label>
                    </div>

                    {!!right && (
                        <div className={`presc-param-select ${attribute}-select right`}>
                            <span id={rightEyeLabelId} style={visuallyHiddenStyles}>
                                Right eye
                            </span>
                            {right?.values.length === 1 ? (
                                <>
                                    <input
                                        type="hidden"
                                        {...register(right?.name)}
                                        value={right?.values[0]}
                                    />
                                    {right?.values[0]}
                                </>
                            ) : (
                                <Controller
                                    name={right?.name}
                                    control={control}
                                    defaultValue=""
                                    render={({ field: { onChange, value } }) => (
                                        <Select
                                            SelectDisplayProps={{
                                                'aria-labelledby': `${rightEyeLabelId} ${fieldLabelId}`,
                                            }}
                                            disabled={!right?.enabled}
                                            value={value}
                                            onChange={onChange}
                                            defaultValue={right?.defaultValue || ''}
                                            className={`param-select`}
                                            IconComponent={props => <ArrowDown {...props} />}
                                            MenuProps={menuOptions}
                                        >
                                            {right?.values.map((v: string | number) => (
                                                <MenuItem key={v} value={v}>
                                                    {v}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            )}
                        </div>
                    )}

                    {!!left && (
                        <div className={`presc-param-select ${attribute}-select left`}>
                            <span id={leftEyeLabelId} style={visuallyHiddenStyles}>
                                Left eye
                            </span>
                            {left?.values.length === 1 ? (
                                <>
                                    <input
                                        type="hidden"
                                        {...register(left?.name)}
                                        value={left?.values[0]}
                                    />
                                    {left?.values[0]}
                                </>
                            ) : (
                                <Controller
                                    name={left?.name}
                                    control={control}
                                    defaultValue=""
                                    render={({ field: { onChange, value } }) => (
                                        <Select
                                            SelectDisplayProps={{
                                                'aria-labelledby': `${leftEyeLabelId} ${fieldLabelId}`,
                                            }}
                                            disabled={!left?.enabled}
                                            value={value}
                                            onChange={onChange}
                                            defaultValue={left?.defaultValue || ''}
                                            className={`param-select`}
                                            IconComponent={props => <ArrowDown {...props} />}
                                            MenuProps={menuOptions}
                                        >
                                            {left?.values.map((v: string | number) => (
                                                <MenuItem key={v} value={v}>
                                                    {v}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default FormColumn;
